import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { GraduationCap, Plus, Pencil, Trash2, X } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { TrainingRecord } from '../../../types/whs';
import { Badge } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
const userLabel = (u: UserData) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

const STATUS_META: Record<'valid' | 'expiring' | 'expired', { label: string; cls: string }> = {
  valid: { label: 'Valid', cls: 'bg-emerald-100 text-emerald-800' },
  expiring: { label: 'Expiring soon', cls: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-800' },
};

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface TrainingForm {
  user_id: string;
  person_name: string;
  training_type: string;
  description: string;
  provider: string;
  completed_date: string;
  expiry_date: string;
  certificate_ref: string;
}

const emptyForm: TrainingForm = {
  user_id: '', person_name: '', training_type: '', description: '',
  provider: '', completed_date: '', expiry_date: '', certificate_ref: '',
};

function TrainingInner() {
  const orgTz = useOrgTimezone();

  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expiringOnly, setExpiringOnly] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TrainingRecord | null>(null);
  const [form, setForm] = useState<TrainingForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listTraining(expiringOnly ? { expiring_within_days: 60 } : {});
    if (res.success) { setRecords(res.data); setError(null); } else setError(res.error.message);
    setLoading(false);
  }, [expiringOnly]);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (r: TrainingRecord) => {
    setEditing(r);
    setForm({
      user_id: r.user_id != null ? String(r.user_id) : '',
      person_name: r.person_name ?? '',
      training_type: r.training_type,
      description: r.description ?? '',
      provider: r.provider ?? '',
      completed_date: r.completed_date ?? '',
      expiry_date: r.expiry_date ?? '',
      certificate_ref: r.certificate_ref ?? '',
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.training_type.trim()) { setError('Training type is required.'); return; }
    setSaving(true);
    const payload: Partial<TrainingRecord> = {
      user_id: form.user_id ? Number(form.user_id) : null,
      person_name: form.user_id ? null : orNull(form.person_name),
      training_type: form.training_type.trim(),
      description: orNull(form.description),
      provider: orNull(form.provider),
      completed_date: orNull(form.completed_date),
      expiry_date: orNull(form.expiry_date),
      certificate_ref: orNull(form.certificate_ref),
    };
    const res = editing
      ? await whsService.updateTraining(editing.id, payload)
      : await whsService.createTraining(payload);
    setSaving(false);
    if (res.success) { setModalOpen(false); setError(null); await load(); }
    else setError(res.error.message);
  };

  const remove = async (r: TrainingRecord) => {
    if (!window.confirm('Delete this training record?')) return;
    const res = await whsService.deleteTraining(r.id);
    if (res.success) await load(); else setError(res.error.message);
  };

  const personName = (r: TrainingRecord) =>
    [r.first_name, r.last_name].filter(Boolean).join(' ') || r.person_name || '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Training & Competency"
        subtitle="Safety inductions, tickets and competencies with expiry tracking"
        icon={<GraduationCap className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
            <input type="checkbox" className="h-4 w-4 rounded border-neutral-300" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} />
            Expiring soon (within 60 days)
          </label>
          <div className="ml-auto">
            <Button onClick={openCreate}><Plus className="h-4 w-4" /> New record</Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Training</th>
                <th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3">Expiry</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Certificate</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-400">No training records found.</td></tr>
              ) : records.map((r) => (
                <tr key={r.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium text-neutral-900">{personName(r)}</td>
                  <td className="px-4 py-3 text-neutral-700">{r.training_type}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.provider || '—'}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.completed_date ? formatLocalDateInTz(r.completed_date, orgTz) : '—'}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.expiry_date ? formatLocalDateInTz(r.expiry_date, orgTz) : '—'}</td>
                  <td className="px-4 py-3">
                    {r.computed_status
                      ? <Badge label={STATUS_META[r.computed_status].label} className={STATUS_META[r.computed_status].cls} />
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{r.certificate_ref || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(r)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(r)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit training record' : 'New training record'}
          onClose={() => setModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={submit} isLoading={saving}>{editing ? 'Save changes' : 'Create'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Employee</label>
              <select className={inputCls} value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                <option value="">— Not a system user —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Person name (if not a user)</label>
              <input className={inputCls} value={form.person_name} disabled={!!form.user_id} onChange={(e) => setForm({ ...form, person_name: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Training type *</label>
              <input className={inputCls} value={form.training_type} onChange={(e) => setForm({ ...form, training_type: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Provider</label>
              <input className={inputCls} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Certificate ref</label>
              <input className={inputCls} value={form.certificate_ref} onChange={(e) => setForm({ ...form, certificate_ref: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Completed date</label>
              <input type="date" className={inputCls} value={form.completed_date} onChange={(e) => setForm({ ...form, completed_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Expiry date</label>
              <input type="date" className={inputCls} value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Description</label>
              <textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function WhsTraining() {
  return <WhsGate><TrainingInner /></WhsGate>;
}
